@file:Suppress("UNCHECKED_CAST_TO_EXTERNAL_INTERFACE")

package org.example.project.enginetabular

import androidx.compose.runtime.*
import org.example.project.settings.UISettings
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*

@Composable
fun TabularHeader(
    visibleColumns: List<String>,
    headerDisplayLabels: Map<String, String> = emptyMap(),
    headerNameToIndex: Map<String, Int>,
    hoveredColIndex: Int?,
    filterTexts: List<MutableState<String>>,
    onColumnOrderChange: (draggedHeader: String, targetHeader: String) -> Unit,
    onHoverColumnChange: (Int?) -> Unit,
    onHeaderClick: ((Int) -> Unit)? = null,
    onHeaderLabelEdit: ((Int, String) -> Unit)? = null,
    sortColumn: String? = null,
    sortAscending: Boolean = true,
    onSortChange: ((String) -> Unit)? = null,
    showFilterRow: Boolean = true,
    headerHeightCss: String? = null,
    filterRowTopCss: String? = null,
    indexColumnWidthCss: String? = null,
    headerFontSizeCss: String? = null,
    dividerColorCss: String? = null,
    dataColumnWidthCss: String? = null,
    accentHeaders: Set<String> = emptySet(),
    smartMode: Boolean = false
) {
    Thead {
        Tr {
            Th({
                classes(TableStyles.th, TableStyles.scrollSnapCell)
                if (smartMode) {
                    classes(TableStyles.smartTh)
                }
                style {
                    headerHeightCss?.let { property("height", it) }
                    headerFontSizeCss?.let { property("font-size", it) }
                    if (smartMode && visibleColumns.isEmpty()) {
                        property("border-right", "1px solid transparent")
                    }
                    indexColumnWidthCss?.let {
                        property("width", it)
                        property("min-width", it)
                        property("max-width", it)
                    }
                    dividerColorCss?.let {
                        property("border-right", "1px solid $it")
                        property("border-bottom", "1px solid $it")
                    }
                }
            }) {
                Text("#")
            }
            visibleColumns.forEach { header ->
                var isEditing by remember(header, headerDisplayLabels[header]) { mutableStateOf(false) }
                var editedText by remember(header, headerDisplayLabels[header]) {
                    mutableStateOf(headerDisplayLabels[header] ?: header)
                }
                val colIdx = headerNameToIndex[header]
                val isAccentHeader = header in accentHeaders
                Th({
                    classes(TableStyles.th, TableStyles.scrollSnapCell)
                    if (smartMode) {
                        classes(TableStyles.smartTh)
                    }
                    attr("draggable", "true")
                    onDragStart { event ->
                        event.dataTransfer?.setData("text/plain", header)
                    }
                    onDragOver { event -> event.preventDefault() }
                    onDrop { event ->
                        val draggedHeader = event.dataTransfer?.getData("text/plain") ?: return@onDrop
                        onColumnOrderChange(draggedHeader, header)
                    }
                    onMouseOver { onHoverColumnChange(colIdx) }
                    onMouseOut { onHoverColumnChange(null) }
                    onClick { colIdx?.let { onHeaderClick?.invoke(it) } }
                    onDoubleClick {
                        if (onHeaderLabelEdit != null && colIdx != null) {
                            isEditing = true
                        }
                    }
                    style {
                        if (hoveredColIndex == colIdx) color(UISettings.ACCENT)
                        headerHeightCss?.let { property("height", it) }
                        headerFontSizeCss?.let { property("font-size", it) }
                        dataColumnWidthCss?.let {
                            property("width", it)
                            property("min-width", it)
                            property("max-width", it)
                        }
                        dividerColorCss?.let {
                            property("border-right", "1px solid $it")
                            property("border-bottom", "1px solid $it")
                        }
                        if (smartMode && header == visibleColumns.lastOrNull()) {
                            property("border-right", "1px solid transparent")
                        }
                    }
                }) {
                    if (isEditing && colIdx != null && onHeaderLabelEdit != null) {
                        Input(InputType.Text, attrs = {
                            classes(TableStyles.input)
                            value(editedText)
                            onInput { editedText = it.value }
                            onBlur {
                                isEditing = false
                                onHeaderLabelEdit(colIdx, editedText)
                            }
                            onKeyDown { event ->
                                when (event.key) {
                                    "Enter" -> {
                                        event.preventDefault()
                                        isEditing = false
                                        onHeaderLabelEdit(colIdx, editedText)
                                    }
                                    "Escape" -> {
                                        event.preventDefault()
                                        isEditing = false
                                        editedText = headerDisplayLabels[header] ?: header
                                    }
                                }
                            }
                        })
                    } else {
                        Span({
                            style {
                                display(DisplayStyle.Block)
                                width(100.percent)
                                overflow("hidden")
                                whiteSpace("nowrap")
                                property("text-overflow", "ellipsis")
                                if (isAccentHeader) {
                                    color(UISettings.ACCENT)
                                    fontWeight("700")
                                    property("text-shadow", "0 0 10px rgba(255,0,0,0.35)")
                                }
                            }
                        }) { Text(headerDisplayLabels[header] ?: header) }
                    }
                    if (onSortChange != null) {
                        val indicator = when {
                            sortColumn == header && sortAscending  -> " ▲"
                            sortColumn == header && !sortAscending -> " ▼"
                            else -> ""
                        }
                        Span({
                            style {
                                property("user-select", "none")
                                cursor("pointer")
                                if (sortColumn == header) color(UISettings.ACCENT)
                                else color(Color("#666"))
                            }
                            onClick { onSortChange(header) }
                        }) { Text(if (indicator.isNotEmpty()) indicator else " ⇅") }
                    }
                }
            }
        }

        if (showFilterRow) {
            Tr({
                classes(TableStyles.filterRow)
                if (smartMode) {
                    classes(TableStyles.smartFilterRow)
                }
            }) {
                Td({
                    classes(TableStyles.td, TableStyles.scrollSnapCell, TableStyles.filterCell)
                    if (smartMode) {
                        classes(TableStyles.smartTd, TableStyles.smartFilterCell)
                    }
                    style {
                        filterRowTopCss?.let { property("top", it) }
                        if (smartMode) {
                            backgroundColor(Color("#1B2028"))
                        }
                        if (smartMode && visibleColumns.isEmpty()) {
                            property("border-right", "1px solid transparent")
                        }
                        indexColumnWidthCss?.let {
                            property("width", it)
                            property("min-width", it)
                            property("max-width", it)
                        }
                    }
                }) { Text("") }
                visibleColumns.forEach { header ->
                    val originalIndex = headerNameToIndex[header] ?: return@forEach
                    Td({
                    classes(TableStyles.td, TableStyles.scrollSnapCell, TableStyles.filterCell)
                    if (smartMode) {
                        classes(TableStyles.smartTd, TableStyles.smartFilterCell)
                    }
                    style {
                        filterRowTopCss?.let { property("top", it) }
                        if (smartMode) {
                            backgroundColor(Color("#1B2028"))
                        }
                        if (hoveredColIndex == originalIndex) property("border", "1px solid ${UISettings.ACCENT}")
                        if (smartMode && header == visibleColumns.lastOrNull()) {
                            property("border-right", "1px solid transparent")
                        }
                    }
                }) {
                        Input(InputType.Text, attrs = {
                            classes(TableStyles.input)
                            value(filterTexts[originalIndex].value)
                            onInput {
                                filterTexts[originalIndex].value = it.value
                            }
                        })
                    }
                }
            }
        }
    }
}
